import React, { useContext } from 'react';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { PlanCard, PlanCardStatusLine } from '../ui/plan-card';
import { TransferStatusCard } from '../ui/transfer-status-card';
import { TransferProgressSummary } from '../ui/transfer-progress-summary';
import { resolvePlanLogo } from '../Navigation/planDisplay';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { formatDateWithLocale } from '../../utils/dateUtils';
import { PlanConDatos } from '../../utils/resumenMulticuenta/agregado';
import { anioDeMadrid, fmtPorcentaje } from '../../utils/resumenMulticuenta/formato';
import { buildTransferProgress } from '../../utils/resumenMulticuenta/progresoTraspaso';
import { transferToCardProps } from '../../utils/traspasos/tarjetasDeTraspaso';

export interface ResumenPlanCardProps {
	plan: PlanConDatos;
	/** Dentro del carrusel de móvil: versión corta. */
	condensed: boolean;
	onVerPlan: (planId: string) => void;
	hoy: Date;
	/** Idioma de la persona, para la fecha de llegada estimada. */
	language: string;
}

const conValores = (texto: string, valores: Record<string, string | number>) =>
	Object.entries(valores).reduce(
		(t, [clave, valor]) => t.replace(`{${clave}}`, String(valor)),
		texto
	);

/**
 * Una card de plan del Resumen multicuenta: `ui/plan-card` de Proa con su traspaso vivo dentro
 * (`ui/transfer-status-card` + `ui/transfer-progress-summary`), como en `Screens/Resumen
 * Multicuenta`, con la rentabilidad del año en curso y la aportación mensual de su plan (Fase A2).
 */
const ResumenPlanCard: React.FC<ResumenPlanCardProps> = ({
	plan,
	condensed,
	onVerPlan,
	hoy,
	language
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const t = (clave: string) => translate(`resumen.planCard.${clave}`);
	const enProceso = plan.estado === 'enProceso';
	const suspendido = plan.estado === 'suspendido';

	// «Pendiente KYC» solo si de verdad falta la identidad; si no, el alta está en manos de operaciones.
	const subtitle = enProceso
		? t(plan.soloLectura ? 'pendingKyc' : 'inProcess')
		: plan.empresa
			? suspendido
				? `${plan.empresa} · ${t('suspended')}`
				: plan.empresa
			: undefined;

	// La línea de estado: el plan suspendido explica qué hacer; el personal sin identidad, por qué
	// no se opera; el resto, su aportación mensual (tú + empresa). Sin dato de aportación, nada.
	const aportacion = plan.aportacion ? plan.aportacion.tu + plan.aportacion.empresa : null;
	const statusLine: PlanCardStatusLine | undefined = suspendido
		? { text: t('suspendedStatus') }
		: plan.soloLectura && !plan.traspaso
			? { text: t('readOnlyStatus'), tone: 'danger' }
			: aportacion === null
				? undefined
				: {
						text:
							aportacion > 0
								? conValores(t('monthlyContribution'), { importe: fmtEur(aportacion) })
								: t('monthlyContributionNone')
					};

	// En el carrusel de móvil, la misma información en la frase corta de Proa.
	const statusLineCorta: PlanCardStatusLine | undefined = suspendido
		? { text: t('condensed.suspended') }
		: plan.soloLectura && !plan.traspaso
			? { text: t('condensed.readOnly'), tone: 'danger' }
			: aportacion === null
				? undefined
				: {
						text:
							aportacion > 0
								? conValores(t('condensed.monthlyContribution'), { importe: fmtEur(aportacion) })
								: t('condensed.monthlyContributionNone')
					};

	// El completado, como en Proa: cuándo llegó y cuánto se sumó, sin la línea «importe desde
	// origen» de un traspaso en marcha. Sin importe conocido (0), solo la fecha.
	const completadoComoProa = (transfer: NonNullable<PlanConDatos['traspaso']>) => {
		const fecha = formatDateWithLocale(transfer.executedAt ?? transfer.updatedAt, language, {
			day: 'numeric',
			month: 'short'
		});
		const importe = parseFloat(transfer.amount);
		return {
			subtitle:
				importe > 0
					? conValores(t('completedTransfer.subtitle'), { fecha, importe: fmtEur(importe) })
					: conValores(t('completedTransfer.subtitleNoAmount'), { fecha }),
			amount: undefined,
			originName: undefined
		};
	};

	// «Pendiente» solo si el traspaso espera algo de la persona: lo decide `pendienteTraspaso`, con la
	// misma regla que la tarjeta de arriba (en empleo nunca, que ahí no se pide nada de esto). Como
	// `ui/PlanCard` en Proa, la etiqueta es solo «Pendiente», el motivo va en el título y lo que hay
	// que hacer, debajo. Suelta, en Movimientos, la tarjeta sigue con su propio copy.
	const tarjeta = plan.traspaso
		? transferToCardProps(plan.traspaso, { kycDone: !plan.pendienteTraspaso }, translate)
		: null;
	const pendiente = plan.pendienteTraspaso ? `pendingTransfer.${plan.pendienteTraspaso}` : null;
	const traspaso =
		tarjeta && pendiente
			? { ...tarjeta, title: t(`${pendiente}.title`), subtitle: '', badge: t('pendingBadge') }
			: tarjeta && plan.traspaso?.status === 'completed'
				? { ...tarjeta, ...completadoComoProa(plan.traspaso) }
				: tarjeta;
	const progreso =
		plan.traspaso?.status === 'in_progress' ? buildTransferProgress(plan.traspaso, hoy) : null;
	const anio = anioDeMadrid(hoy);

	const valor = enProceso
		? undefined
		: plan.saldoEuros === null
			? t('noData')
			: fmtEur(plan.saldoEuros);

	return (
		<PlanCard
			icon={
				<img
					src={resolvePlanLogo(plan.nombre, plan.gestora)}
					alt=""
					className="h-9 w-9 rounded-control border border-border bg-card object-contain p-1"
				/>
			}
			name={plan.nombre ?? t('unnamedPlan')}
			// Solo en la columna de escritorio: la del carrusel se monta a la vez y repetiría el id.
			nameId={condensed ? undefined : `resumen-plan-${plan.id}`}
			subtitle={subtitle}
			badge={{ label: t(`kind.${plan.tipo}`) }}
			valueLabel={enProceso ? undefined : t('totalSaved')}
			valueLabelShort={enProceso ? undefined : t('totalSavedShort')}
			value={valor}
			// La del año en curso, como en Proa («Rentabilidad 2026»); la acumulada es la de las cifras
			// grandes. Sin dato (o cargando) no se pinta: nunca un 0 %.
			rentabilidadCaption={conValores(t('profitabilityYear'), { anio })}
			rentabilidadCaptionShort={conValores(t('profitabilityYearShort'), { anio })}
			rentabilidadLabel={
				plan.rentabilidadAnio
					? fmtPorcentaje(plan.rentabilidadAnio.porcentaje, language)
					: undefined
			}
			statusLine={statusLine}
			ctaLabel={plan.soloLectura ? t('viewDetailReadOnly') : t('viewDetail')}
			onCtaClick={() => onVerPlan(plan.id)}
			readOnly={plan.soloLectura}
			condensed={condensed}
			condensedStatus={traspaso ? { text: traspaso.title } : statusLineCorta}
		>
			{traspaso && (
				// Con progreso, el origen va en su línea («VidaCaixa · día 6…»): en la cabecera saldría
				// dos veces (es lo que pide el JSDoc de `ui/transfer-progress-summary`).
				<TransferStatusCard
					{...traspaso}
					originName={progreso ? undefined : traspaso.originName}
					defaultExpanded
					compact
				>
					{/* 🔴 Sin nada que enseñar, sin hijos: la tarjeta pinta su bloque de detalle (con borde)
					    en cuanto `children` es verdadero, y `[false, false]` lo es. */}
					{(pendiente || progreso) && (
						<>
							{pendiente && (
								<p className="text-sm text-muted-foreground">{t(`${pendiente}.detail`)}</p>
							)}
							{progreso && (
								<TransferProgressSummary
									originName={traspaso.originName ?? ''}
									dayRangeLabel={conValores(translate('resumen.transferProgress.day'), {
										dia: progreso.dia
									})}
									etaLabel={
										progreso.fueraDePlazo
											? translate('resumen.transferProgress.overdue')
											: conValores(translate('resumen.transferProgress.eta'), {
													fecha: formatDateWithLocale(progreso.eta.toISOString(), language, {
														day: 'numeric',
														month: 'short'
													})
												})
									}
									progressPct={progreso.progressPct}
								/>
							)}
						</>
					)}
				</TransferStatusCard>
			)}
		</PlanCard>
	);
};

export default ResumenPlanCard;
